// src/components/avatar/AvatarWithOutfit.js
//
// A student's avatar picture in its circle, wearing their shop items:
// wings behind, then the picture, frame, glasses, hat, and a pet peeking in
// at the bottom corner. Positions come from SLOT_BOX in wearableArt.js, in %
// of the circle, so the same outfit fits a 40px toolbar icon and a 200px
// shop preview.
//
//   <AvatarWithOutfit avatarUrl={...} outfit={{ hat: "hat-crown" }} size={120} />
//   tryOn={item}  — shows that item in its slot instead (shop preview)
//   look={...}    — an avatar-maker look (avatarParts.js); used instead of
//                   the old picture when present
//
// Items come from the live shop catalogue (useShopItems); pass `items` to
// skip loading it.

import React from "react";
import { ItemArt, SLOT_BOX, SLOT_ORDER, hatDrop } from "./wearableArt";
import { AvatarFace, DEFAULT_LOOK, FULL_H } from "./avatarParts";
import { useShopItems } from "../../services/shopService";
import "./avatar.css";

// Full body (`full`): the top 100 units are the same as the portrait, so hats
// stay put; the rest move to where the smaller chest / the feet are. Frames
// are a ring around the portrait circle, so they don't show in full body.
const FULL_BOX = {
  wings: { left: -42, top: -14, size: 184, behind: true },
  pet: { left: 60, top: 100, size: 60, behind: false },
  pin: { left: 53.3, top: 71.8, size: 12.4, behind: false }, // the chest is a little narrower (TORSO_SCALE_X)
  necklace: { left: 31.4, top: 59.6, size: 37.2, behind: false },
};

/**
 * `size` is the height. Portrait: a size x size circle. `full`: the whole
 * body, size tall and size x 100/170 wide.
 */
// eslint-disable-next-line no-unused-vars
const AvatarWithOutfit = ({ look = null, avatarUrl, outfit = {}, size = 64, tryOn = null, items, className = "", title, full = false, onClick, noBg = false }) => {
  const shop = useShopItems(!items);
  const byId = items || shop.byId;

  const worn = {};
  SLOT_ORDER.forEach((slot) => {
    // the avatar maker draws its own glasses/sunglasses right on the eyes
    if (look && slot === "glasses") return;
    const id = outfit?.[slot];
    if (id && byId[id]) worn[slot] = byId[id];
  });
  if (tryOn?.kind && !(look && tryOn.kind === "glasses")) worn[tryOn.kind] = tryOn;

  const layer = (slot) => {
    const item = worn[slot];
    if (!item) return null;
    if (full && slot === "frame") return null;
    const b = (full && FULL_BOX[slot]) || SLOT_BOX[slot];
    const k = full ? 100 / FULL_H : 1; // vertical % per unit
    return (
      <span
        key={slot}
        className={`sbl-av-layer sbl-av-${slot} ${b.behind ? "is-behind" : ""}`}
        style={{ left: `${b.left}%`, top: `${(b.top + (slot === "hat" ? hatDrop(item) : 0)) * k}%`, width: `${b.size}%`, height: `${b.size * k}%` }}
      >
        <ItemArt item={item} />
      </span>
    );
  };

  return (
    <span
      className={`sbl-av ${full ? "is-full" : ""} ${onClick ? "is-clickable" : ""} ${className}`}
      style={{ width: full ? Math.round((size * 100) / FULL_H) : size, height: size }}
      title={title}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick(e)) : undefined}
    >
      {SLOT_ORDER.filter((s) => SLOT_BOX[s].behind).map(layer)}
      <span className="sbl-av-face">
        {/* Old picture avatars are retired: everyone is drawn by the avatar
            maker. Someone who hasn't built one yet shows the starter look. */}
        <AvatarFace look={look || DEFAULT_LOOK} full={full} noBg={noBg} />
      </span>
      {SLOT_ORDER.filter((s) => !SLOT_BOX[s].behind).map(layer)}
    </span>
  );
};

export default AvatarWithOutfit;
